import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';
import { lazy, Suspense } from 'react';
import { EventDetailSkeleton } from '@/components/events/EventsSkeletons';
import { Skeleton } from '@/components/ui/skeleton';
import { isUuid } from '@/lib/public-event-path';

const EventDetail = lazy(() => import('@/pages/EventDetail'));
// The public, sessionless ticket page (86d45muck). Split from EventDetail so
// an outside buyer never downloads the member chunk.
const PublicEventPage = lazy(() => import('@/pages/PublicEventPage'));

// Router search values are JSON-parsed, so `?cancelled=1` arrives as the
// number 1. Normalise to a string so the schema stays `string | undefined`.
const optionalString = z.preprocess(
  (value) => (value === undefined || value === null ? undefined : String(value)),
  z.string().optional(),
);

const eventDetailSearchSchema = z.object({
  // Canonical occurrence slot (toOccurrenceKey, e.g. 2026-06-18T11:42:00Z) when
  // viewing a single session of a recurring series. Omitted for the series view
  // and for single events.
  occ: z.string().optional(),
  // Public ticket page only: Stripe Checkout's success return carries
  // `?session_id=cs_…`, the cancel return `?cancelled=1`.
  session_id: optionalString,
  cancelled: optionalString,
});

export const Route = createFileRoute('/events/$eventId')({
  validateSearch: (search) => eventDetailSearchSchema.parse(search),
  component: EventRoute,
});

/**
 * `/events/<param>` is two pages: a UUID is the authed member event page, a
 * slug is the public ticket page (no session, no shell — see
 * lib/public-event-path.ts and the root gate lists in routes/__root.tsx).
 */
function EventRoute() {
  const { eventId } = Route.useParams();
  const { session_id, cancelled } = Route.useSearch();

  if (isUuid(eventId)) {
    return (
      // Layout-matched skeleton (not a spinner) so a slow chunk download and the
      // page's own data-fetch skeleton read as ONE continuous loading state.
      <Suspense fallback={<EventDetailSkeleton />}>
        <EventDetail />
      </Suspense>
    );
  }

  return (
    <Suspense fallback={<PublicEventRouteSkeleton />}>
      <PublicEventPage
        slug={eventId}
        sessionId={session_id ?? null}
        cancelled={cancelled === '1'}
      />
    </Suspense>
  );
}

/** Chunk-download placeholder for the layoutless public page. */
function PublicEventRouteSkeleton() {
  return (
    <div className="flex min-h-screen min-h-[100svh] items-start justify-center bg-background px-4 py-8 sm:px-6 sm:py-12">
      <div aria-busy="true" aria-label="Loading event" className="w-full max-w-lg space-y-4">
        <div className="overflow-hidden rounded-xl border border-border bg-card/80">
          <Skeleton className="aspect-video w-full rounded-none" />
          <div className="space-y-4 p-5 sm:p-6">
            <Skeleton className="h-3 w-24" />
            <Skeleton className="h-7 w-3/4" />
            <Skeleton className="h-4 w-2/3" />
            <Skeleton className="h-4 w-1/2" />
          </div>
        </div>
        <div className="space-y-3 rounded-xl border border-border bg-card/80 p-5 sm:p-6">
          <Skeleton className="h-5 w-28" />
          <Skeleton className="h-11 w-full" />
          <Skeleton className="h-11 w-full" />
          <Skeleton className="h-11 w-full" />
        </div>
      </div>
    </div>
  );
}
